/*
 * Shot-type radar — the seven tracked shot types (see Radar).
 *
 * Each axis is the type's share WITHIN its group: the three offensive axes sum
 * to 100% among themselves, the four defensive likewise. Values come from the
 * JSON's offensive_shots_ratio / defensive_shots_ratio (built in the report).
 * Swords mark the offensive vertices (top), Shield the defensive (bottom).
 */

import { useId, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Shield, HelpCircle, ChevronDown } from 'lucide-react';
import Radar, { type RadarSeries } from './Radar';
import { SHOT_TYPE_CATALOG } from './shotTypes';

export type RadarDatum = RadarSeries;

const AXES = SHOT_TYPE_CATALOG;

const EASE = [0.22, 1, 0.36, 1] as const;

// Collapsible glossary under the pentagon — closed by default so the chart
// stays clean; the toggle reads as quiet chart chrome (same "?" affordance as
// the metric info buttons elsewhere in the report).
const LegendDisclosure: React.FC = () => {
  const { t } = useTranslation('tournament');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="glossary">
      <button
        type="button"
        className="glossary-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <HelpCircle size={11.5} strokeWidth={2.2} />
        <span>{t('shotLegend.toggle')}</span>
        <motion.span
          className="glossary-chev"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.26, ease: EASE }}
        >
          <ChevronDown size={12} strokeWidth={2.2} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            id={panelId}
            className="glossary-reveal"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE }}
          >
            {/* dev drives the glossary off the axis catalog (so lobs get an
                entry too); the class names are the shared `glossary-*` block
                the court's outcome legend also uses. */}
            <ul className="glossary-list tagged">
              {AXES.map((axis, i) => (
                <motion.li
                  key={axis.key}
                  className="glossary-item"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.08 + i * 0.055,
                      duration: 0.28,
                      ease: EASE,
                    },
                  }}
                >
                  <span className="glossary-term">
                    {t(`label.${axis.lbl}` as never) as string}
                  </span>
                  {/* Which half of the radar this axis sits in. The same two
                      glyphs mark the offensive and defensive halves on the
                      chart itself, so the tag reads as a pointer back to it. */}
                  <span className={`shot-tag ${axis.group}`}>
                    {axis.group === 'offensive' ? (
                      <Swords size={9} strokeWidth={2.4} />
                    ) : (
                      <Shield size={9} strokeWidth={2.4} />
                    )}
                    {t(`label.${axis.group}` as never) as string}
                  </span>
                  <span className="glossary-def">
                    {t(`shotLegend.${axis.lbl}` as never) as string}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ShotRadar: React.FC<{ data: RadarDatum[] }> = ({ data }) => {
  const { t } = useTranslation('tournament');
  const axes = AXES.map((axis) => ({
    label: t(`label.${axis.lbl}` as never) as string,
    Icon: axis.group === 'offensive' ? Swords : Shield,
  }));
  return (
    <>
      {/* Turn the wheel by half a step so axes 0 and 1 — the two aerial
          shots — straddle the top symmetrically and sit level with each
          other, instead of one pointing straight up and the other hanging
          off its shoulder. Derived from the axis count, so it stays right if
          an axis is ever added. */}
      <Radar
        axes={axes}
        data={data}
        topIcon={Swords}
        bottomIcon={Shield}
        rotate={-180 / AXES.length}
      />
      <LegendDisclosure />
    </>
  );
};

export default ShotRadar;
